import { PaymentStatus } from './../models/payment-status.enum';
import { Injectable } from '@angular/core';
import { Payment } from '../models/payment';
import { PaymentHttpService } from './payment.http.service';
import { Observable } from 'rxjs/Observable';
import { LogService } from '../../shared/log/log.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { TranslateService } from '@ngx-translate/core';
import { List } from 'immutable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { PaymentStatusUtils } from 'app/event/utils/payment-status-utils';
import { PaymentType } from '../models/payment-type';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { EventStore } from './event.store';
import { OnyxEvent } from '../models/event';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { catchError, flatMap, map, take } from 'rxjs/operators';

@Injectable()
export class PaymentStore {

  // This is the same as BehaviorSubject but without a default value
  private paymentList: ReplaySubject<List<Payment>> = new ReplaySubject<List<Payment>>(1);
  private latestFinalPayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(1);
  private latestPrepayment: ReplaySubject<Payment> = new ReplaySubject<Payment>(1);
  private finalPaymentStatusList: ReplaySubject<Payment[]> = new ReplaySubject<Payment[]>(1);

  constructor(private paymentHttpService: PaymentHttpService,
              private logService: LogService,
              private flashMessagesService: FlashMessagesService,
              private translateService: TranslateService,
              private environment: EnvironmentConfiguration,
              private eventStore: EventStore,
              private propertyLookupStore: PropertyLookupStore) {
  }

  addPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.add(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        if (payment.type === PaymentType.PREPAYMENT) {
          this.flashMessagesService.success('EVENT.PREPAYMENT.PREPAYMENT_SAVED');
        } else {
          this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        }
        return result;
      });
  }

  updateFinalPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.update(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_PAYMENT', `Error updating Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        return result;
      });
  }

  addBulkActionPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.add(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        return result;
      });
  }

  updateBulkActionPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.update(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_PAYMENT', `Error updating Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        return result;
      });
  }

  cancelNewPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.cancelNew(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_PAYMENT', `Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        if (payment.type === PaymentType.PREPAYMENT) {
          this.flashMessagesService.success('EVENT.PREPAYMENT.PREPAYMENT_SAVED');
        } else {
          this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        }
        return result;
      });
  }

  cancelExistingPayment(payment: Payment): Observable<any> {
    return this.paymentHttpService.cancelExisting(payment)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.UPDATE_PAYMENT', `Error updating Payment: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.COMMISSION.PAYMENT_SAVED');
        return result;
      });
  }

  get payments(): Observable<List<Payment>> {
    return this.paymentList.asObservable();
  }

  getLatestFinalPayment(): Observable<Payment> {
    return this.latestFinalPayment.asObservable();
  }

  loadPayments(eventId: number): Observable<List<Payment>> {
    return this.paymentHttpService.getPayments(eventId)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PAYMENTS'));
        this.logService.error(`Error adding Payment: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Payment>) => {
        this.paymentList.next(result);
        return result;
      });
  }

  loadLatestFinalPayment(eventId: number): Observable<Payment> {
    return this.paymentHttpService.getLatestFinalPayment(eventId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_LATEST_PAYMENT'));
      this.logService.error(`Error getting last Payment: ${error}`);
      return Observable.throw(error);
    }).map((result: Payment) => {
      this.latestFinalPayment.next(result);
      return result;
    });
  }

  get commissionTotal(): Observable<number> {
    return this.payments.map(payments => {
      return payments
        .filter(payment => !this.isPaymentRejected(payment))
        .map(payment => payment.commissionAmount)
        .reduce((prev: number, current: number) => !!current ? prev + current : prev, 0);
    });
  }

  get containsRejectedPayments(): Observable<boolean> {
    return this.payments.map(payments => payments.some(this.isPaymentRejected));
  }

  /**
   * Gets the payment with the latest created date
   */
  get currentPayment(): Observable<Payment> {
    return this.payments.map((list: List<Payment>) => {
      return list.max((payment1: Payment, payment2: Payment) => {
        if (payment1.createdDate < payment2.createdDate) {
          return -1;
        } else if (payment1.createdDate > payment2.createdDate) {
          return 1;
        } else {
          return 0;
        }
      });
    });
  }

  /**
   * Returns true if the current payment exists and does not have a rejected or error status
   * @returns {Observable<R>}
   */
  get isCurrentPaymentValid(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return !!lastPayment && !this.isPaymentRejected(lastPayment);
    });
  }

  private isPaymentRejected(payment: Payment): boolean {
    if (payment) {
      return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.REQUEST_REJECTED;
    } else {
      return false;
    }
  }

  get isLastPaymentRequested(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment && this.isPaymentRequested(lastPayment);
    });
  }

  private isPaymentRequested(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.REQUESTED;
  }

  get isLastPaymentRequestResentOrRequestRejected(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment && (this.isPaymentRequestResent(lastPayment) || this.isPaymentRequestRejected(lastPayment));
    });
  }

  get isLastPaymentRequestedOrRequestResentOrRequestRejected(): Observable<boolean> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment && (this.isPaymentRequested(lastPayment)
                              || this.isPaymentRequestResent(lastPayment)
                              || this.isPaymentRequestRejected(lastPayment));
    });
  }

  get lastPaymentStatus(): Observable<PaymentStatus> {
    return this.currentPayment.map((lastPayment: Payment) => {
      return lastPayment.status;
    });
  }

  private isPaymentRequestResent(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.RESENT_REQUEST;
  }

  public isPaymentRequestRejected(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status) || payment.status === PaymentStatus.REQUEST_REJECTED;
  }

  public isPaymentRequestedOrRequestResent(payment: Payment): boolean {
    return PaymentStatusUtils.isError(payment.status)
            || payment.status === PaymentStatus.REQUESTED
            || payment.status === PaymentStatus.RESENT_REQUEST;
  }

  getPrepayment(): Observable<Payment> {
    return this.latestPrepayment.asObservable();
  }

  loadLatestPrepayment(eventId: number): Observable<Payment> {
    return this.paymentHttpService.getLatestPrepayment(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_PREPAYMENT'));
        this.logService.error(`Error getting Prepayment: ${error}`);
        return Observable.throw(error);
      })
      .map((result: Payment) => {
        this.latestPrepayment.next(result);
        return result;
      });
  }

  loadFinalPaymentStatusList(eventId: number): Observable<Payment[]> {
    return this.paymentHttpService.getFinalPaymentStatusList(eventId).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_FINAL_PAYMENT_STATUS'));
      this.logService.error(`Error getting list of final payment status: ${error}`);
      return Observable.throw(error);
    }).map((result: Payment[]) => {
      this.finalPaymentStatusList.next(result);
      return result;
    });
  }

  getFinalPaymentStatusList(): Observable<Payment[]> {
    return this.finalPaymentStatusList.asObservable();
  }

  resendPaymentRequest(eventId: number) {
    return this.paymentHttpService.resendPaymentRequest(eventId).catch(error => {
      this.logService.error(`Error resending payment request: ${error}`);
      return Observable.throw(error);
    }).map((result: Response) => {
      this.loadLatestFinalPayment(eventId).take(1).subscribe();
      this.loadFinalPaymentStatusList(eventId).take(1).subscribe();
      return result;
    });
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/payment.store.ts